import find from "lodash/find";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "~/components/shared/DropdownMenu";
import EmojiAvatar from "~/components/shared/EmojiAvatar";
import Emojify from "~/components/shared/Emojify";
import Icon from "~/components/shared/Icon";
import classNames from "classnames";

type Props = {
  currentClusterId?: string;
  unclustered: boolean;
  unclusteredPath: string | null;
  newClusterPath: string | null;
  clusters: Array<{
    id: string;
    name: string;
    path: string;
    color: string;
    emoji: string;
  }>;
};

export default function ClusterPicker({
  currentClusterId,
  unclustered,
  unclusteredPath,
  newClusterPath,
  clusters,
}: Props) {
  const currentCluster = find(clusters, { id: currentClusterId });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <button
          type="button"
          className="unstyled-button gap1.5 flex w-full min-w-0 max-w-[270px] items-center text-left"
          data-testid="clusters-dropdown-trigger"
        >
          <EmojiAvatar
            emoji={currentCluster?.emoji}
            color={currentCluster?.color}
            fallbackIcon={unclustered ? "custom/outline/broken-cluster" : "custom/outline/cluster"}
            className="shrink-0"
          />

          <Emojify
            className="semi-bold charcoal-900 m-0 min-w-0 flex-1 truncate text-lg leading-tight"
            text={unclustered ? "Unclustered" : currentCluster?.name}
          />

          <Icon icon="down-triangle" className="shrink-0" style={{ width: 7, height: 7 }} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent
          align="start"
          className="w-[270px] min-w-0 max-w-[calc(100vw-20px)] p-0"
          data-testid="clusters-dropdown-content"
        >
          <div className="max-h-[80vh] overflow-y-auto py-[5px]">
            {unclusteredPath && (
              <div className="border-gray mb-1 border-b">
                <DropdownMenuItem
                  asChild={true}
                  active={unclustered}
                  className={classNames(
                    "btn black hover-purple focus-purple semi-bold gap1.5 m-1 flex w-auto items-center rounded-sm px-1 py-1",
                    {
                      "btn--activated purple bg-light-purple semi-bold": unclustered,
                    },
                  )}
                >
                  <a href={unclusteredPath} aria-current={unclustered ? "page" : undefined}>
                    <EmojiAvatar
                      emoji={null}
                      color={unclustered ? "#FFFFFF" : null}
                      fallbackIcon="custom/outline/broken-cluster"
                      className="very-dark-gray shrink-0 border border-white"
                    />

                    <Emojify text="Unclustered" className="min-w-0 flex-1 truncate" />
                  </a>
                </DropdownMenuItem>
              </div>
            )}

            {clusters.map((cluster) => (
              <DropdownMenuItem
                key={cluster.path}
                asChild={true}
                active={currentClusterId === cluster.id}
                className={classNames(
                  "btn black hover-purple focus-purple semi-bold gap1.5 mx-1 flex w-auto items-center rounded-sm px-1 py-1",
                  {
                    "btn--activated purple bg-light-purple semi-bold":
                      currentClusterId === cluster.id,
                  },
                )}
              >
                <a
                  href={cluster.path}
                  aria-current={currentClusterId === cluster.id ? "page" : undefined}
                >
                  <EmojiAvatar
                    emoji={cluster.emoji}
                    color={cluster.color}
                    fallbackIcon="custom/outline/cluster"
                    className="very-dark-gray shrink-0 border border-white"
                  />

                  <Emojify text={cluster.name} className="min-w-0 flex-1 truncate" />
                </a>
              </DropdownMenuItem>
            ))}

            {newClusterPath !== null ? (
              <DropdownMenuItem
                asChild={true}
                className="btn black hover-purple focus-purple semi-bold gap1.5 m-1 flex w-auto items-center rounded-sm px-1 py-1"
              >
                <a href={newClusterPath}>
                  <div
                    className="flex shrink-0 items-center justify-center bg-gray-200"
                    style={{ width: "40px", height: "40px", borderRadius: "50%" }}
                  >
                    <Icon
                      icon="heroicons/outline/plus"
                      className="flex-none"
                      style={{ width: 24, height: 24 }}
                    />
                  </div>
                  New Cluster
                </a>
              </DropdownMenuItem>
            ) : null}
          </div>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
